import type { JobAdsRequest } from "@/modules/jobAds/client/api/types"
import type { JobAdsFetch } from "@/modules/jobAds/client/hooks/useJobAdsFetchContext"

import { useQuery } from "@tanstack/react-query"
import { useSearchParams } from "next/navigation"

import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { getAssociatedCompaniesIds } from "@/modules/companies/client/utils/getAssociatedCompaniesId"
import { getJobAds } from "@/modules/jobAds/client/api/getJobAds"
import { getJobAdsFromESQueryKeys } from "@/modules/jobAds/client/api/getJobAdsFromESQueryKeys"
import { DEFAULT_RADIUS_KM, QUERY_CACHE_TIME, QUERY_STALE_TIME } from "@/modules/jobAds/client/constants"
import { computeJobAdsRequest } from "@/modules/jobAds/client/utils/computeJobAdsRequest"
import { getJobAdsSearchParamsFromURLSearchParams } from "@/modules/jobAds/client/utils/getJobAdsSearchParamsFromURLSearchParams"
import { useLocale } from "@/modules/locales/useLocale"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"
import { useCurrentUserSearchConfigFetch } from "@/modules/user/client/hooks/useCurrentUserSearchConfigFetch"

export const useJobAdsFetch = (): JobAdsFetch => {
  const currentLocale = useLocale()
  const searchParams = useSearchParams()

  const {
    page,
    userQuery,
    contractDurations,
    contractTypes,
    locations,
    positionCategories,
    startDates,
    studyLevels,
    exclusive,
    tags,
    locales,
    companySectors,
  } = getJobAdsSearchParamsFromURLSearchParams(searchParams)

  const groupOffers = searchParams.getAll("groupOffers")

  const { dataCompany } = useCompanyFetch()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCurrentUserSearchConfig } = useCurrentUserSearchConfigFetch()

  const companyIds = dataCompany?.id
    ? [dataCompany.id, ...getAssociatedCompaniesIds(!groupOffers.length, dataCompany)]
    : []
  const contractTypesSearchConfig = dataCurrentUserSearchConfig?.data.relationships.searchFilters.data.filter(
    filter => filter.name === "contract"
  )[0]
  const studyLevelsSearchConfig = dataCurrentUserSearchConfig?.data.relationships.searchFilters.data.filter(
    filter => filter.name === "study_levels"
  )[0]

  let jobAdsRequest: JobAdsRequest = {}

  if (dataCurrentUser && dataCurrentUserSearchConfig) {
    jobAdsRequest = computeJobAdsRequest({
      companyIds,
      companySectors,
      contractDurations,
      contractTypes,
      contractTypesSearchConfig,
      curriculumIds: dataCurrentUser?.attributes.curriculumIds,
      exclusive,
      locales,
      page,
      positionCategories,
      schoolId: dataCurrentUser?.relationships.careerCenter.data.id,
      startDates,
      studyLevels,
      studyLevelsSearchConfig,
      tags,
      userQuery,
      ...(locations && { locations, radiusKm: DEFAULT_RADIUS_KM }),
    })
  }

  const jobAdsQueryKey = getJobAdsFromESQueryKeys({
    companyIds: jobAdsRequest.company_ids,
    companySectors,
    contractDurations,
    contractTypes,
    curriculumIds: dataCurrentUser?.attributes.curriculumIds,
    exclusive,
    locales,
    locations,
    page,
    positionCategories,
    schoolId: dataCurrentUser?.relationships.careerCenter.data.id || "0",
    startDates,
    studyLevels,
    tags,
    userQuery,
  })

  const {
    data: dataJobAds,
    isPending: isLoadingJobAds,
    isSuccess: isSuccessJobAds,
    isError: isErrorJobAds,
  } = useQuery({
    enabled: !!dataCurrentUser && !!dataCurrentUserSearchConfig,
    gcTime: QUERY_CACHE_TIME,
    placeholderData: previousData => (!dataCurrentUser || !dataCurrentUserSearchConfig ? previousData : undefined),
    queryFn: () => getJobAds(jobAdsRequest, currentLocale),
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: jobAdsQueryKey,
    staleTime: QUERY_STALE_TIME,
  })

  return {
    dataJobAds,
    isErrorJobAds,
    isLoadingJobAds,
    isSuccessJobAds,
  }
}
